import { Avatar, Tag, Button, Typography, Flex } from "antd";
import ava1 from "shared/assets/images/logo-shopify.svg";
import { getRoleColor } from "../constants";
import { formatDateTime } from "shared/utils/datetime";
import { renderRoleTags, renderOrganizationTags } from "../utils/renderHelpers";
import { servicesUrlConfig } from "shared/config/setting";

const { Title } = Typography;

/**
 * Create organization members table columns
 * @param {Function} translate - Translation function (defaults to identity function)
 * @param {Object} options - Configuration options
 * @param {boolean} options.showActions - Whether to show actions column
 * @param {Function} options.onEdit - Edit handler
 * @param {Function} options.onDelete - Delete handler
 * @returns {Array} Array of column definitions
 */
export const createOrgMembersColumns = (
  translate = (key) => key,
  options = {},
) => {
  const columns = [
    {
      title: translate("organizations.username"),
      dataIndex: "username",
      key: "username",
      width: "32%",
      render: (text, record) => (
        <Avatar.Group>
          <Avatar
            className="shape-avatar"
            shape="square"
            size={40}
            src={ava1}
          />
          <div className="avatar-info">
            <Title level={5}>{record?.user?.username}</Title>
          </div>
        </Avatar.Group>
      ),
    },
    {
      title: translate("organizations.roles"),
      dataIndex: "roles",
      key: "roles",
      render: (roles) => renderRoleTags(roles, translate),
    },
  ];

  if (options.showActions) {
    columns.push({
      title: "",
      key: "actions",
      render: (_, record) => (
        <Flex justify="center" align="center">
          {options.onEdit && (
            <Button type="link" onClick={() => options.onEdit(record)}>
              {translate("organizations.edit")}
            </Button>
          )}
          {options.onDelete && (
            <Button
              type="link"
              danger
              onClick={() => options.onDelete(record.user.username)}
            >
              {translate("common.delete")}
            </Button>
          )}
        </Flex>
      ),
    });
  }

  return columns;
};

/**
 * Create organization robots table columns
 * @param {Function} translate - Translation function (defaults to identity function)
 * @param {Object} options - Configuration options
 * @param {boolean} options.showActions - Whether to show actions column
 * @param {Function} options.onDelete - Delete handler
 * @param {Function} options.onDoubleClick - Double click handler
 * @returns {Array} Array of column definitions
 */
export const createOrgRobotColumns = (
  translate = (key) => key,
  options = {},
) => {
  const columns = [
    {
      title: translate("organizations.robot"),
      dataIndex: "robot_id",
      key: "robot_id",
      render: (text, robotRecord) => (
        <div className="avatar-info">
          <Avatar.Group>
            <Avatar
              className="shape-avatar"
              shape="square"
              size={40}
              src={servicesUrlConfig.ROBOTS_API.getStaticUrl(
                robotRecord?.icon_path ??
                  robotRecord?.robot_type?.icon_path ??
                  "/robot_type_icons/unknown_robot_type.png",
              )}
            />
            <div className="avatar-info">
              <Title level={5}>{robotRecord.robot_id}</Title>
              <p>{robotRecord.name || translate("organizations.noname")}</p>
            </div>
          </Avatar.Group>
        </div>
      ),
    },
    {
      title: translate("organizations.type"),
      dataIndex: "robot_type",
      key: "robot_type",
      render: (text, record) => <span>{record?.robot_type?.name || ""}</span>,
    },
  ];

  if (options.showActions) {
    columns.push({
      title: "",
      key: "actions",
      render: (_, record) => (
        <Flex justify="center" align="center">
          {options.onDelete && (
            <Button
              type="link"
              danger
              onClick={() => options.onDelete(record.robot_id)}
            >
              {translate("common.delete")}
            </Button>
          )}
        </Flex>
      ),
    });
  }

  return columns;
};

/**
 * Create robot table columns for robot management pages
 * @param {OrganizationColorManager} organizationColorManager - Color manager for organization tags
 * @param {Function} translate - Translation function (defaults to identity function)
 * @param {Object} options - Configuration options
 * @param {boolean} options.showOwner - Whether to show owner column
 * @param {boolean} options.showActions - Whether to show actions column
 * @param {Function} options.onEdit - Edit handler
 * @param {Function} options.onDelete - Delete handler
 * @returns {Array} Array of column definitions
 */
export const createRobotColumns = (
  organizationColorManager,
  translate = (key) => key,
  options = {},
) => {
  const columns = [
    {
      title: "ROBOT",
      dataIndex: "robot_id",
      key: "robot_id",
      width: "32%",
      render: (text, robotRecord) => (
        <div className="avatar-info">
          <Avatar.Group>
            <Avatar
              className="shape-avatar"
              shape="square"
              size={40}
              src={servicesUrlConfig.ROBOTS_API.getStaticUrl(
                robotRecord?.icon_path ??
                  robotRecord?.robot_type?.icon_path ??
                  "/robot_type_icons/unknown_robot_type.png",
              )}
            />
            <div className="avatar-info">
              <Title level={5}>{robotRecord.robot_id}</Title>
              <p>{robotRecord.name || "noname"}</p>
            </div>
          </Avatar.Group>
        </div>
      ),
    },
    {
      title: "Organizations",
      dataIndex: "organizations",
      key: "organizations",
      render: (text, robotRecord) => {
        const orgs = robotRecord?.organizations || [];
        return renderOrganizationTags(orgs, organizationColorManager);
      },
    },
  ];

  if (options.showOwner) {
    columns.push({
      title: "OWNER",
      key: "created_by",
      dataIndex: "created_user",
      render: (text, robotRecord) => (
        <span>{robotRecord?.created_user?.username || ""}</span>
      ),
    });
  }

  columns.push(
    {
      title: "STATUS",
      key: "status",
      dataIndex: "status",
      render: (text, robotRecord) => {
        const status = (robotRecord.status || "OFFLINE").toLowerCase();
        const isOnline = status === "online";
        return (
          <Button
            type={isOnline ? "primary" : "default"}
            className={isOnline ? "tag-primary" : "tag-badge"}
          >
            {status}
          </Button>
        );
      },
    },
    {
      title: "Last Login",
      key: "last_login_at",
      dataIndex: "last_login_at",
      render: (text, robotRecord) => (
        <div className="ant-employed">
          <span>{formatDateTime(robotRecord?.last_login_at)}</span>
        </div>
      ),
    },
  );

  if (options.showActions) {
    columns.push({
      title: "",
      key: "actions",
      render: (_, record) => (
        <Flex justify="center" align="center">
          {options.onEdit && (
            <Button type="link" onClick={() => options.onEdit(record)}>
              Edit
            </Button>
          )}
          {options.onDelete && (
            <Button
              type="link"
              danger
              onClick={() => options.onDelete(record.robot_id)}
            >
              Delete
            </Button>
          )}
        </Flex>
      ),
    });
  }

  return columns;
};

/**
 * Create organizations table columns
 * @param {Function} translate - Translation function
 * @param {Object} options - Configuration options
 * @returns {Array} Array of column definitions
 */
export const createOrgsColumns = (translate = (key) => key, options = {}) => {
  const columns = [
    {
      title: translate("organizations.orgName"),
      dataIndex: "name",
      key: "name",
      width: "32%",
      render: (text, record) => {
        const orgData = options.isMyOrgs ? record.organization : record;
        return (
          <Avatar.Group>
            <Avatar
              className="shape-avatar"
              shape="square"
              size={40}
              src={ava1}
            />
            <div className="avatar-info">
              <Title level={5}>{orgData.name}</Title>
            </div>
          </Avatar.Group>
        );
      },
    },
    {
      title: translate("organizations.description"),
      dataIndex: "description",
      key: "description",
      render: (text, record) => {
        const orgData = options.isMyOrgs ? record.organization : record;
        return <span>{orgData.description || ""}</span>;
      },
    },
    {
      title: translate("organizations.createdBy"),
      key: "created_user",
      dataIndex: options.isMyOrgs ? "organization" : "created_user",
      render: (text, record) => {
        const orgData = options.isMyOrgs ? record.organization : record;
        return <span>{orgData.created_user}</span>;
      },
    },
  ];

  if (options.isMyOrgs) {
    columns.push({
      title: translate("organizations.myRoles"),
      key: "my_roles",
      dataIndex: "roles",
      render: (text, record) => renderRoleTags(record.roles, translate),
    });
  }

  if (options.showActions) {
    columns.push({
      title: "",
      key: "action",
      render: (_, record) => {
        const canEdit = options.isMyOrgs
          ? record.roles.includes("admin")
          : true;

        return (
          <Flex justify="center" align="center">
            {options.onEdit && (
              <Button
                type="link"
                onClick={() =>
                  options.onEdit(
                    options.isMyOrgs ? record.organization : record,
                  )
                }
                disabled={!canEdit}
              >
                {translate("organizations.edit")}
              </Button>
            )}
            {options.onDelete && (
              <Button
                type="link"
                danger
                onClick={() =>
                  options.onDelete(
                    options.isMyOrgs ? record.organization : record,
                  )
                }
                disabled={!canEdit}
              >
                {translate("common.delete")}
              </Button>
            )}
          </Flex>
        );
      },
    });
  }

  return columns;
};
